import type {
  TransactionEvent,
  TracesSamplerSamplingContext
} from "@sentry/core";

/** Default production sample rate for non-critical, non-noisy routes. */
export const SENTRY_PRODUCTION_TRACES_SAMPLE_RATE = 0.02;

/** Higher rate for checkout / visit / invoice flows. */
export const SENTRY_CRITICAL_TRACES_SAMPLE_RATE = 0.05;

/** Lower rate for high-volume list / registry / calendar navigations. */
export const SENTRY_NOISY_TRACES_SAMPLE_RATE = 0.01;

export const SENTRY_NON_PRODUCTION_TRACES_SAMPLE_RATE = 1;

const CRITICAL_TRANSACTION_PREFIXES: readonly string[] = [
  "/organization/visit",
  "/organization/check-out",
  "/organization/check-in",
  "/organization/invoices"
];

const NOISY_TRANSACTION_PREFIXES: readonly string[] = [
  "/organization/patients-registry",
  "/organization/customers-registry",
  "/organization/calendar",
  "/organization/board",
  "/organization/dashboard"
];

const DROP_TRANSACTION_PREFIXES: readonly string[] = ["/locked"];

function matchesPrefix(name: string, prefixes: readonly string[]): boolean {
  return prefixes.some(
    (prefix: string) => name === prefix || name.startsWith(`${prefix}/`)
  );
}

export function shouldDropSentryTransactionName(name: string): boolean {
  return matchesPrefix(name, DROP_TRANSACTION_PREFIXES);
}

export function resolveSentryTracesSampleRate(transactionName: string): number {
  if (shouldDropSentryTransactionName(transactionName)) {
    return 0;
  }

  if (matchesPrefix(transactionName, CRITICAL_TRANSACTION_PREFIXES)) {
    return SENTRY_CRITICAL_TRACES_SAMPLE_RATE;
  }

  if (
    transactionName === "/" ||
    matchesPrefix(transactionName, NOISY_TRANSACTION_PREFIXES)
  ) {
    return SENTRY_NOISY_TRACES_SAMPLE_RATE;
  }

  return SENTRY_PRODUCTION_TRACES_SAMPLE_RATE;
}

export function sampleSentryTrace(
  context: TracesSamplerSamplingContext,
  isProduction: boolean
): number {
  if (!isProduction) {
    return SENTRY_NON_PRODUCTION_TRACES_SAMPLE_RATE;
  }

  const rate: number = resolveSentryTracesSampleRate(context.name);

  return context.inheritOrSampleWith(rate);
}

export function filterSentryTransaction(
  event: TransactionEvent
): TransactionEvent | null {
  const name: string = event.transaction ?? "";

  if (shouldDropSentryTransactionName(name)) {
    return null;
  }

  return event;
}
